import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { Clerk } from "@clerk/clerk-js";
import { ReferenceViewer } from "./ReferenceViewer";
import { Loader2, FileSpreadsheet, AlertCircle } from "lucide-react";

const API_BASE_URL = process.env.API_BASE_URL || "https://api-test.getrima.ai";
const CLERK_PUBLISHABLE_KEY = process.env.CLERK_PUBLISHABLE_KEY || "pk_test_Y3VycmVudC1zY29ycGlvbi04NC5jbGVyay5hY2NvdW50cy5kZXYk";

console.log("[Rima Excel] Initializing...");
console.log("[Rima Excel] API URL:", API_BASE_URL);

interface CellReference {
  cell: string;
  sheet: string;
  value: string | number | null;
}

interface RunInfo {
  runId: string;
  runName?: string;
}

interface AuthSession {
  token: string;
  userEmail: string;
  expiresAt: number;
}

const App: React.FC = () => {
  const [clerk, setClerk] = useState<Clerk | null>(null);
  const [clerkLoading, setClerkLoading] = useState(true);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  // Initialize Clerk
  useEffect(() => {
    const initClerk = async () => {
      try {
        const clerkInstance = new Clerk(CLERK_PUBLISHABLE_KEY);
        await clerkInstance.load({
          // Disable routing to avoid history API issues in Office Add-ins
          routerPush: () => {},
          routerReplace: () => {},
        });
        setClerk(clerkInstance);
        console.log("[Rima Excel] Clerk loaded");

        // Check if already signed in
        if (clerkInstance.user) {
          const token = await clerkInstance.session?.getToken();
          const email = clerkInstance.user.primaryEmailAddress?.emailAddress;
          if (token && email) {
            const newSession: AuthSession = {
              token,
              userEmail: email,
              expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days
            };
            setSession(newSession);
            localStorage.setItem("rima_excel_auth", JSON.stringify(newSession));
            console.log("[Rima Excel] Already signed in as:", email);
          }
        } else {
          // Check for cached session
          const savedSession = localStorage.getItem("rima_excel_auth");
          if (savedSession) {
            try {
              const parsed = JSON.parse(savedSession) as AuthSession;
              if (parsed.expiresAt > Date.now()) {
                setSession(parsed);
                console.log("[Rima Excel] Restored cached session for:", parsed.userEmail);
              } else {
                localStorage.removeItem("rima_excel_auth");
              }
            } catch (e) {
              localStorage.removeItem("rima_excel_auth");
            }
          }
        }
      } catch (e) {
        console.error("[Rima Excel] Failed to load Clerk:", e);
        setAuthError("Failed to initialize authentication. Please refresh.");
      } finally {
        setClerkLoading(false);
      }
    };

    initClerk();
  }, []);

  // Listen for Clerk user changes
  useEffect(() => {
    if (!clerk) return undefined;

    const handleUserChange = async (): Promise<void> => {
      if (clerk.user) {
        const token = await clerk.session?.getToken();
        const email = clerk.user.primaryEmailAddress?.emailAddress;
        if (token && email) {
          const newSession: AuthSession = {
            token,
            userEmail: email,
            expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
          };
          setSession(newSession);
          localStorage.setItem("rima_excel_auth", JSON.stringify(newSession));
          console.log("[Rima Excel] Signed in as:", email);
        }
      }
    };

    const unsubscribe = clerk.addListener(handleUserChange);
    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [clerk]);

  const handleSignOut = async () => {
    if (clerk) {
      await clerk.signOut();
    }
    setSession(null);
    localStorage.removeItem("rima_excel_auth");
    console.log("[Rima Excel] Signed out");
  };

  if (clerkLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3 text-zinc-400">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (!session) {
    return <LoginScreen clerk={clerk} error={authError} />;
  }

  return <AuthenticatedApp session={session} onSignOut={handleSignOut} />;
};

// Login screen component with embedded Clerk
function LoginScreen({ clerk, error }: { clerk: Clerk | null; error: string | null }) {
  const signInRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (clerk && signInRef.current && !mounted) {
      signInRef.current.innerHTML = "";
      clerk.mountSignIn(signInRef.current, {
        appearance: {
          elements: {
            rootBox: { width: "100%" },
            card: { boxShadow: "none", border: "1px solid #e5e7eb" },
          },
        },
      });
      setMounted(true);
    }

    return () => {
      if (clerk && mounted) {
        clerk.unmountSignIn(signInRef.current!);
      }
    };
  }, [clerk, mounted]);

  return (
    <div className="flex flex-col h-full bg-zinc-50 font-sans">
      {/* Header */}
      <header className="px-6 py-5 bg-white border-b border-zinc-200">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <FileSpreadsheet className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-zinc-900">Rima Excel</h1>
            <p className="text-xs text-zinc-500">Sign in to get started</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        )}

        {/* Clerk Sign In */}
        <div ref={signInRef} className="w-full">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-6 py-3 bg-white border-t border-zinc-200 text-center">
        <p className="text-xs text-zinc-400">Powered by Rima AI</p>
      </footer>
    </div>
  );
}

// Main authenticated app
function AuthenticatedApp({ session, onSignOut }: { session: AuthSession; onSignOut: () => void }) {
  const [selectedCell, setSelectedCell] = useState<CellReference | null>(null);
  const [sourceData, setSourceData] = useState<any>(null);
  const [loadingSource, setLoadingSource] = useState(false);
  const [runInfo, setRunInfo] = useState<RunInfo | null>(null);
  const [loadingRunInfo, setLoadingRunInfo] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Read hidden sheet for run ID on mount
  useEffect(() => {
    readHiddenSheet();
  }, []);

  // Setup cell selection listener
  useEffect(() => {
    const setupCellSelectionHandler = async () => {
      try {
        await Office.onReady();

        Office.context.document.addHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          handleSelectionChanged,
          (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              console.error("Failed to add selection handler:", result.error);
            }
          }
        );

        handleSelectionChanged();
      } catch (e) {
        console.error("Failed to setup Office handlers:", e);
      }
    };

    setupCellSelectionHandler();

    return () => {
      try {
        Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
          handler: handleSelectionChanged,
        });
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, []);

  // Fetch source data when cell changes
  useEffect(() => {
    if (runInfo && selectedCell) {
      fetchSourceData(selectedCell);
    }
  }, [runInfo, selectedCell?.cell, selectedCell?.sheet]);

  // Read the hidden sheet to get run ID
  const readHiddenSheet = async () => {
    setLoadingRunInfo(true);
    setError(null);

    try {
      await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        sheets.load("items/name,items/visibility");
        await context.sync();

        // Log all sheets for debugging
        console.log("[Rima Excel] All sheets in workbook:");
        for (const sheet of sheets.items) {
          console.log(`  - "${sheet.name}" (visibility: ${sheet.visibility})`);
        }

        // Look for hidden sheet "_recon_meta"
        let foundSheet: Excel.Worksheet | null = null;

        for (const sheet of sheets.items) {
          const sheetNameLower = sheet.name.toLowerCase();
          if (sheetNameLower === "_recon_meta") {
            console.log("[Rima Excel] Found _recon_meta sheet!");
            foundSheet = sheet;
            break;
          }
        }

        // Fallback: try any hidden sheet
        if (!foundSheet) {
          console.log("[Rima Excel] _recon_meta not found, looking for any hidden sheet...");
          for (const sheet of sheets.items) {
            if (
              sheet.visibility === Excel.SheetVisibility.hidden ||
              sheet.visibility === Excel.SheetVisibility.veryHidden
            ) {
              console.log(`[Rima Excel] Found hidden sheet: "${sheet.name}"`);
              foundSheet = sheet;
              break;
            }
          }
        }

        if (foundSheet) {
          const range = foundSheet.getRange("A1:B10");
          range.load("values");
          await context.sync();

          const values = range.values;
          console.log("[Rima Excel] Sheet values:", JSON.stringify(values));

          let runId: string | null = null;
          let runName: string | null = null;

          for (const row of values) {
            const key = String(row[0]).toLowerCase();
            const value = String(row[1]);
            if (key === "run_id" || key === "runid") {
              runId = value;
            } else if (key === "run_name" || key === "runname") {
              runName = value;
            }
          }

          if (!runId && values[0][0]) {
            runId = String(values[0][0]);
          }

          if (runId && runId !== "undefined" && runId !== "null") {
            console.log("[Rima Excel] Found run ID:", runId);
            setRunInfo({ runId, runName: runName || undefined });
          } else {
            console.log("[Rima Excel] No run_id found in sheet values");
            setError("No run ID found. Please open a file from Rima Work Manager.");
          }
        } else {
          console.log("[Rima Excel] No hidden sheet found");
          setError("This file doesn't contain Rima metadata. Please open a file from Work Manager.");
        }
      });
    } catch (e) {
      console.error("[Rima Excel] Failed to read hidden sheet:", e);
      setError("Failed to read file metadata. Please try again.");
    } finally {
      setLoadingRunInfo(false);
    }
  };

  const handleSelectionChanged = useCallback(async () => {
    try {
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        range.load(["address", "values", "worksheet"]);
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load("name");

        await context.sync();

        const address = range.address;
        const cellRef = address.includes("!") ? address.split("!")[1] : address;
        const sheetName = sheet.name;
        const value = range.values[0]?.[0] ?? null;

        setSelectedCell({
          cell: cellRef,
          sheet: sheetName,
          value: value,
        });
      });
    } catch (e) {
      console.error("Failed to get selected cell:", e);
    }
  }, []);

  const fetchSourceData = async (cellRef: CellReference) => {
    if (!runInfo) return;

    setLoadingSource(true);
    setSourceData(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/recon/source`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.token}`,
        },
        body: JSON.stringify({
          run_id: runInfo.runId,
          cell_id: cellRef.cell,
        }),
      });

      console.log("[Rima Excel] API Response status:", response.status);
      console.log("[Rima Excel] API Response Content-Type:", response.headers.get("Content-Type"));

      if (!response.ok) {
        if (response.status === 404) {
          setSourceData({ notFound: true });
        } else if (response.status === 401) {
          // Token expired, sign out
          onSignOut();
        } else {
          throw new Error(`API error: ${response.status}`);
        }
        return;
      }

      const contentType = response.headers.get("Content-Type") || "";

      // Handle image response (API returns image directly)
      if (contentType.startsWith("image/")) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        console.log("[Rima Excel] Received image, created blob URL:", imageUrl);
        setSourceData({
          image_url: imageUrl,
          document_name: `Source for ${cellRef.cell}`,
        });
        return;
      }

      // Handle JSON response
      const data = await response.json();
      console.log("[Rima Excel] API Response data:", JSON.stringify(data, null, 2));

      // Check if API returned success but no source found
      if (data.status === 404 || data.found === false || data.success === false) {
        console.log("[Rima Excel] API returned not found in response body");
        setSourceData({ notFound: true });
        return;
      }

      setSourceData(data);
    } catch (e) {
      console.error("Failed to fetch source data:", e);
      setSourceData({ error: true });
    } finally {
      setLoadingSource(false);
    }
  };

  if (loadingRunInfo) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3 text-zinc-400">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="text-sm">Reading file metadata...</span>
        </div>
      </div>
    );
  }

  if (error || !runInfo) {
    return (
      <div className="flex flex-col h-full bg-zinc-50 font-sans">
        <header className="px-5 py-4 bg-white border-b border-zinc-200">
          <div className="flex items-center justify-between">
            <h1 className="text-[15px] font-semibold text-zinc-900">Rima Excel</h1>
            <span className="text-xs text-zinc-500">{session.userEmail}</span>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="flex flex-col items-center gap-4 text-center max-w-sm">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-amber-500" />
            </div>
            <div>
              <p className="text-lg font-semibold text-zinc-900">No Rima File Detected</p>
              <p className="text-sm text-zinc-500 mt-2">
                {error || "Please open an Excel file that was exported from Rima Work Manager."}
              </p>
            </div>
            <button
              onClick={readHiddenSheet}
              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Retry Detection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ReferenceViewer
      runInfo={runInfo}
      selectedCell={selectedCell}
      sourceData={sourceData}
      loading={loadingSource}
      loadingData={false}
      dataLoaded={true}
      onRefresh={() => selectedCell && fetchSourceData(selectedCell)}
      onSignOut={onSignOut}
      userEmail={session.userEmail}
    />
  );
}

export default App;
